import React, { useState } from "react";
import ChannelIcon from "./ChannelIcon";
import Tooltip from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ChannelEngagementTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'score',
    direction: 'desc'
  });

  // Don't render table if no data
  if (!data || data.length === 0) {
    return null;
  }

  // Sort function
  const sortedData = [...data].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get the appropriate sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Activity badge color based on level
  const getActivityBadgeColor = (activity) => {
    switch (activity.toLowerCase()) {
      case 'high':
      case 'very high':
        return 'bg-[#DAFBEB] text-black-0';
      case 'medium':
        return 'bg-[#FBF0DA] text-black-0';
      case 'low':
      case 'very low':
        return 'bg-[#FBDADA] text-black-0';
      case 'extreme':
        return 'bg-[#C4F5DC] text-black-0';
      default:
        return 'bg-grey-20 text-grey-60';
    }
  };

  return (
    <div>
      <h3 className="text-md font-normal text-black-0 px-10 py-3">Channel engagement</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-20">
          <thead>
            <tr>
              <th 
                onClick={() => requestSort('channel')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                Channel {getSortDirectionIndicator('channel')}
              </th>
              <th 
                onClick={() => requestSort('score')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Score {getSortDirectionIndicator('score')}
                  <Tooltip content="Overall engagement score (0–100)." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('activity')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Activity {getSortDirectionIndicator('activity')}
                  <Tooltip content="Activity level bucket based on message volume and participation." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('peakActivity')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Peak activity {getSortDirectionIndicator('peakActivity')}
                  <Tooltip content="Most active time window for the channel within the selected period." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('messages')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Messages {getSortDirectionIndicator('messages')}
                  <Tooltip content="Total messages observed in the selected period." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('reactions')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Reactions {getSortDirectionIndicator('reactions')}
                  <Tooltip content="Total reactions across messages in the selected period." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th
                onClick={() => requestSort('threads')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Threads {getSortDirectionIndicator('threads')}
                  <Tooltip content="Total threads started in the selected period." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('contributors')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Contributors {getSortDirectionIndicator('contributors')}
                  <Tooltip content="Number of unique contributors who posted messages in the selected period." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-20">
            {sortedData.map((channel) => (
              <tr key={channel.id} className="hover:bg-white-f">
                <td className="pl-10 pr-5 py-3 text-sm font-medium text-black-0">
                  <div className="flex items-center gap-1">
                    <ChannelIcon isPrivate={channel.is_private} size={14} />
                    <span>{channel.channel}</span>
                  </div>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal text-black-0">
                  {channel.score}%
                </td>
                <td className="pl-10 pr-5 py-3 text-sm">
                  <span className={`px-2 py-1 rounded-md text-xs font-normal ${getActivityBadgeColor(channel.activity)}`}>
                    {channel.activity}
                  </span>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm text-black-0">
                  {channel.peakActivity || 'N/A'}
                </td>
                <td className="pl-10 pr-5 py-3 text-sm text-black-0">
                  {channel.messages.toLocaleString()}
                </td>
                <td className="pl-10 pr-5 py-3 text-sm text-black-0">
                    {(channel.reactions || 0).toLocaleString()}
                </td>
                <td className="pl-10 pr-5 py-3 text-sm text-black-0">
                    {(channel.threads || 0).toLocaleString()}
                </td>
                <td className="pl-10 pr-5 py-3 text-sm text-black-0">
                    {(channel.contributors || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelEngagementTable; 